import { Title, Caption, Paragraph } from "@/components/ui/typography";
import Tag from "./Tag";
import Image from "next/image";
import Wiremax from "@/public/wire-transfer.svg";
import { CalendarIcon, TruckIcon } from "@heroicons/react/24/outline";
import { Copy, Check } from "lucide-react";
import { Package } from "@/lib/SystemIcons";
import { Button } from "@/components/ui/button";
import ViewWireDetails from "./ViewWireDetails";
import Recipt from "./Recipt";
import { useSelector } from "react-redux";
import { formatDate, formatDateInMonthNameDateAndYear } from "@/app/utils/utility";
import CustomDrawer from "@/components/Drawer";
import { useState } from "react";
import useOrders from "@/app/hooks/useOrders";
import { useShowSnackbar } from "@/app/hooks/useSnackbar";

export default function orderDetails({ orderData, timeLeft }) {
  const { orderDetails } = useSelector(state => state.order);
  const [isDrawerOpen, setDrawerOpen] = useState(false);
  const [copyClicked, setCopyClicked] = useState(false);
  const { requestPaymentRefund } = useOrders();
  const { showSnackbar } = useShowSnackbar();

  //total quantity added to the screen
  const totalQuantity = orderDetails?.OrderProducts?.reduce((total, product) => {
    return total + (product.quantity || 0); // Add quantity, default to 0 if undefined
  }, 0);

  const toggleDrawer = open => {
    setDrawerOpen(open);
  };
  const handleRefundRequest = async refundPayload => {
    const payloadToSend = refundPayload;
    payloadToSend["sl_id"] = orderDetails.sl_id;
    payloadToSend["order_id"] = orderDetails.id;
    const response = await requestPaymentRefund(payloadToSend);
    if (response === "Refund request mail sent") {
      showSnackbar(response, "success");
    }
    return response;
  };

  const shouldShowReciptButton = payments => {
    return payments?.some(payment => payment.status === "pending") || payments?.length === 0;
  };

  const shouldShowPendingPaymentStatus = (payments, order) => {
    return (
      (payments?.some(payment => payment.status === "pending") || payments?.length === 0) &&
      order?.current_status !== "confirmed"
    );
  };

  const isPaymentPaid = payments => {
    return payments?.some(payment => payment?.status === "paid") || payments?.length === 0;
  };
  const shouldShowRequestRefundButton = (payments, order) => {
    let hasStoreCredit = false;
    let hasPartialOrder = false;
    // Iterate through each payment and check their status
    payments.forEach(payment => {
      if (payment.status === "refund_requested") {
        hasStoreCredit = true;
      }
      if (order.current_status === "cancelled") {
        hasPartialOrder = true;
      }
    });

    // Return true if both conditions are met
    return hasStoreCredit && hasPartialOrder;
  };

  const refundButtonText = () => {
    let ButtonText = "Request refund";
    if (orderDetails.EcomUserBank && Object.keys(orderDetails.EcomUserBank).length > 0) {
      ButtonText = "View bank details";
    }
    return ButtonText;
  };

  return (
    <div>
      <div className="flex flex-col md:flex-row items-start md:items-center justify-between ">
        <Title size="lg" className="flex gap-x-2 text-midnightIndigo-400 lg:mb-4">
          #{orderData?.display_order_id || orderDetails?.display_order_id}
          <div className="ml-4 flex items-center gap-2">
            {orderDetails?.current_status !== "pending" &&
              orderDetails?.current_status !== "partial_order" &&
              orderData?.current_status !== "partial_order" && (
                <Tag
                  label={
                    orderData?.isSockAlert || orderDetails?.isSockAlert
                      ? "Stock issue"
                      : orderDetails?.is_partial_order
                        ? "Partial Order"
                        : (orderData?.current_status || orderDetails?.current_status)
                            ?.split("_") // Split the string by underscores
                            .map(word => word.charAt(0).toUpperCase() + word.slice(1)) // Capitalize the first letter of each word
                            .join(" ")
                  }
                  type={
                    (orderData?.current_status || orderDetails?.current_status) === "Stock issue" ||
                    (orderData?.current_status || orderDetails?.current_status) === "cancelled" ||
                    orderData?.isSockAlert ||
                    orderDetails?.isSockAlert
                      ? "error"
                      : "success"
                  }
                />
              )}
          </div>
        </Title>

        {/* *****************************************************NEED TO ADD TIMER HERE AS WELL************************************************************************************ */}

        <div className="flex items-center gap-10 justify-between w-full md:w-auto ">
          {isPaymentPaid(orderDetails?.Payments) && <Tag label="Paid" type="success" />}
          {shouldShowPendingPaymentStatus(orderDetails?.Payments) && <Tag label="Payment pending" type="warning" />}
          {(orderData?.current_status || orderDetails?.current_status) === "cancelled" &&
            orderDetails?.Payments.some(
              payment =>
                payment.status === "refund_requested" ||
                payment.status === "store_credit" ||
                payment.status === "refunded"
            ) && (
              <Tag
                label={
                  orderDetails?.Payments.find(
                    payment =>
                      payment.status === "refund_requested" ||
                      payment.status === "store_credit" ||
                      payment.status === "refunded"
                  )?.status === "refund_requested"
                    ? "Refund Requested"
                    : orderDetails?.Payments.find(
                          payment =>
                            payment.status === "refund_requested" ||
                            payment.status === "store_credit" ||
                            payment.status === "refunded"
                        )?.status === "refunded"
                      ? "Refunded"
                      : "Store Credit"
                }
                type={
                  orderDetails?.Payments.find(
                    payment =>
                      payment.status === "refund_requested" ||
                      payment.status === "store_credit" ||
                      payment.status === "refunded"
                  )?.status === "refunded"
                    ? "success"
                    : "warning"
                }
              />
            )}

          {(orderData?.payments?.length > 0 || orderDetails?.payments?.length > 0) &&
            (orderData?.Payments[0]?.status === "store_credit" ||
              orderDetails?.Payments[0]?.status === "store_credit") && (
              <Tag
                label={(orderData?.Payments[0]?.status || orderDetails?.Payments[0]?.status)
                  ?.split("_") // Split the string by underscores
                  .map(word => word.charAt(0).toUpperCase() + word.slice(1)) // Capitalize the first letter of each word
                  .join(" ")}
                type={"warning"}
              />
            )}

          <div className="flex">
            {orderDetails?.Payments &&
              orderDetails?.Payments[0]?.expire_time &&
              shouldShowPendingPaymentStatus(orderDetails?.Payments, orderDetails) &&
              orderDetails?.Payments[0]?.payment_method === "wire" && (
                <>
                  <Caption size="sm">
                    {timeLeft} {timeLeft !== "Time is up!" && "left"}
                  </Caption>
                </>
              )}
            {orderDetails?.Payments && orderDetails?.Payments[0]?.payment_method === "wire" && (
              <Image src={Wiremax} alt="Wiremax" height={22} width={32} className="ml-3" />
            )}
          </div>
        </div>
      </div>

      <div className="flex  flex-row  justify-between md:items-center items-start">
        <div className="flex flex-col md:flex-row gap-2 md:gap-10">
          <div className="flex flex-col">
            <div className="mb-2 block md:hidden">
              <ViewWireDetails variant="lg" className={"flex md:hidden !outline-none -ml-1 md:ml-0"} />
            </div>
            <div className="flex flex-row items-center md:items-start gap-1 md:gap-0 md:flex-col">
              <div className="flex text-midnightIndigo-300 text-[15px] lg:mb-1">
                <CalendarIcon className="h-[20px] w-[20px] text-midnightIndigo-400 mr-2 " /> Date
              </div>
              <Paragraph className="text-midnightIndigo-400 flex">
                {formatDateInMonthNameDateAndYear(orderDetails?.order_date)}{" "}
                {/*I suggest to use some library for format like date-fns or moment */}
              </Paragraph>
            </div>
          </div>
          <div className="flex flex-row items-center gap-2 md:items-start md:gap-0 md:flex-col">
            <div className="flex text-midnightIndigo-300 text-[15px] lg:mb-1">
              <Package className="h-[20px] w-[20px] mr-2 text-midnightIndigo-400" />
              Quantity
            </div>
            <Paragraph className="text-midnightIndigo-400 flex"> {totalQuantity} products</Paragraph>
          </div>
          <div className="flex flex-row items-center md:items-start gap-2 md:gap-0 md:flex-col">
            <div className="flex text-midnightIndigo-300 lg:mb-1">
              <TruckIcon className="h-[20px] w-[20px] mr-2 text-midnightIndigo-400" />{" "}
              <span className="mr-2 text-[15px]">Tracking Number</span>{" "}
            </div>
            <Paragraph className="flex flex-col items-center md:flex-row text-midnightIndigo-400">
              {orderDetails?.Shipping?.tracking_no || "-"}
              {orderDetails?.Shipping?.tracking_no && (
                <span>
                  {copyClicked ? (
                    <Check className="h-[15px] w-[15px] inline ml-2 cursor-pointer" />
                  ) : (
                    <Copy
                      onClick={() => {
                        navigator.clipboard.writeText(orderDetails?.Shipping?.tracking_no);
                        setCopyClicked(true);
                        setTimeout(() => {
                          setCopyClicked(false);
                        }, 2000);
                      }}
                      className="h-[15px] w-[15px] inline ml-2 cursor-pointer"
                    />
                  )}
                </span>
              )}
            </Paragraph>
          </div>
        </div>

        <CustomDrawer
          isDrawerOpen={isDrawerOpen}
          toggleDrawer={toggleDrawer}
          handleRefundRequest={handleRefundRequest}
          orderDetails={orderDetails}
          setDrawerOpen={setDrawerOpen}
        />
        {orderDetails?.Payments && shouldShowRequestRefundButton(orderDetails?.Payments, orderDetails) && (
          <Button
            variant="outline"
            size="sm"
            className={`${refundButtonText() === "View bank details" ? "!w-[152px]" : "!w-[122px]"} leading-[0]`}
            onClick={() => toggleDrawer(true)}
          >
            {refundButtonText()}
          </Button>
        )}
        {orderDetails?.Payments && shouldShowReciptButton(orderDetails?.Payments, orderDetails) && (
          <div className="flex flex-row gap-5 items-center">
            <ViewWireDetails className=" hidden !outline-none  lg:flex" />
            <Recipt orderDetails={orderDetails} />
          </div>
        )}
      </div>
    </div>
  );
}
